// S7 — Mis acciones (auditoría personal del supervisor).
//
// 🚧 PENDIENTE refactor visual a detalle (sesión 2026-05-28).
// Ajuste mínimo aplicado para coherencia con el canon (S1/S5/S6, C1/C2/C3/C5/C6):
//   - Header consistente (título + subtítulo + botones top-right con variant=outline size=sm)
//   - Tokens semánticos (text-fg / text-fg-tertiary / text-fg-secondary / text-crit/warn/ok)
//   - Loading/error states alineados
//   - Sin colores hardcoded (text-red-XXX, bg-gray-XXX, etc.)
//
// NO se agregó lógica nueva, KPI cards con Sparkline ni gráfico Recharts —
// eso queda para la revisión a detalle por pantalla con HTML+PNG del diseño.
//
// Útil para que el supervisor pueda defender su gestión: "¿qué hiciste con
// X registro?". Lista cronológica de validaciones, invalidaciones, capturas
// por delegación, ediciones, cierres de paro.

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { AccionSupervisor } from 'schemas';
import { Card, CardContent } from '../../components/ui/card';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { useSupervisorMisAcciones } from '../../lib/api/queries';

const RANGOS: { value: '1' | '7' | '30'; label: string }[] = [
  { value: '1', label: 'Hoy (24h)' },
  { value: '7', label: '7 días' },
  { value: '30', label: '30 días' },
];

export function S7MisAccionesPage() {
  const { state } = useAuth();
  const [dias, setDias] = useState<'1' | '7' | '30'>('7');
  const { data, isLoading, isError, error } = useSupervisorMisAcciones(Number(dias));

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor">
      <div className="flex flex-col gap-4">
        <header className="flex items-baseline justify-between border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Mis acciones</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              {data
                ? `${data.total} acción(es) en los últimos ${data.dias} día(s) (${data.rango.desde} a ${data.rango.hasta})`
                : 'Cargando…'}
            </p>
          </div>
        </header>

        <section>
          <SegmentedControl value={dias} onChange={setDias} options={RANGOS} />
        </section>

        {/* Totales por tipo */}
        {data && Object.keys(data.totales_por_tipo).length > 0 ? (
          <section className="flex flex-wrap gap-2">
            {Object.entries(data.totales_por_tipo).map(([tipo, count]) => (
              <span
                key={tipo}
                className="rounded-full bg-surface-overlay px-3 py-1 text-xs text-fg-secondary"
              >
                {prettyTipo(tipo)}: <strong className="text-fg">{count}</strong>
              </span>
            ))}
          </section>
        ) : null}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <Spinner className="size-6" />
            <p className="text-sm text-fg-tertiary">Cargando acciones…</p>
          </div>
        ) : isError || !data ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-crit">
              {error instanceof Error ? error.message : 'No pudimos cargar sus acciones'}
            </CardContent>
          </Card>
        ) : data.acciones.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-fg-tertiary">
              Sin acciones registradas en este rango.
            </CardContent>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.acciones.map((accion) => (
              <AccionRow key={accion.id} accion={accion} />
            ))}
          </ul>
        )}

        <footer className="text-center text-[11px] text-fg-tertiary">
          S7 · auditoría personal · datos de eventos_auditoria
        </footer>
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// AccionRow
// =============================================================================

function AccionRow({ accion }: { accion: AccionSupervisor }) {
  const fechaIso = accion.created_at;
  const fecha = new Date(fechaIso);
  const fechaCorta = `${String(fecha.getDate()).padStart(2, '0')}/${String(fecha.getMonth() + 1).padStart(2, '0')}`;
  const horaHHMM = `${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`;

  return (
    <li>
      <Card className={cn('border', colorPorTipo(accion.tipo_evento))}>
        <CardContent className="flex items-start gap-3 p-3">
          <div className="shrink-0 text-center">
            <p className="text-[10px] uppercase tracking-wider text-fg-tertiary">{fechaCorta}</p>
            <p className="font-mono text-sm font-semibold">{horaHHMM}</p>
          </div>
          <div className="flex-1 border-l border-border pl-3">
            <p className="text-sm font-medium text-fg">{accion.label_accion}</p>
            {accion.contexto ? <p className="text-xs text-fg-tertiary">{accion.contexto}</p> : null}
            {accion.razon ? (
              <p className="mt-1 text-xs italic text-fg-secondary">Razón: {accion.razon}</p>
            ) : null}
          </div>
          <span
            className={cn(
              'shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold uppercase',
              badgeTipo(accion.tipo_evento),
            )}
          >
            {prettyTipo(accion.tipo_evento)}
          </span>
        </CardContent>
      </Card>
    </li>
  );
}

function prettyTipo(tipo: string): string {
  switch (tipo) {
    case 'create':
      return 'Captura';
    case 'update':
      return 'Edición';
    case 'soft_delete':
      return 'Invalidación';
    case 'restore':
      return 'Restauración';
    default:
      return tipo;
  }
}

function colorPorTipo(tipo: string): string {
  switch (tipo) {
    case 'create':
      return 'border-ok';
    case 'update':
      return 'border-border';
    case 'soft_delete':
      return 'border-warn';
    default:
      return 'border-border';
  }
}

function badgeTipo(tipo: string): string {
  switch (tipo) {
    case 'create':
      return 'bg-ok-soft text-ok';
    case 'update':
      return 'bg-surface-overlay text-fg-secondary';
    case 'soft_delete':
      return 'bg-warn-soft text-warn';
    default:
      return 'bg-surface-overlay text-fg-secondary';
  }
}
